import { Record, Tuple } from "record-tuple";

import assert from "@repo/utils/assert";
import EventEmitter from "@repo/utils/EventEmitter";
import UArray from "@repo/utils/UArray";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UTuple from "@repo/utils/UTuple";

import Css from "./Css.mjs";

namespace UHTMLElement {
  export function parents(element: HTMLElement) {
    const parents: HTMLElement[] = [];

    (function getParents(el: HTMLElement | null) {
      if (!el) return;

      parents.push(el);
      getParents(el.parentElement);
    })(element);

    return parents;
  }

  export function scrollParents(element: HTMLElement) {
    const parents: HTMLElement[] = [];

    (function getParents(el: HTMLElement | null) {
      if (!el) return;

      if (el.scrollHeight > el.clientHeight) parents.push(el);

      getParents(el.parentElement);
    })(element);

    return parents;
  }
  scrollParents.listen = (element: HTMLElement, handler: () => void) => {
    const parents = UHTMLElement.scrollParents(element);
    for (const parent of parents) {
      parent.addEventListener("scroll", handler);
    }
    return {
      unlisten() {
        for (const parent of parents) {
          parent.removeEventListener("scroll", handler);
        }
      },
    };
  };

  export function computeTotalTransitionMs(element: HTMLElement) {
    const computed = getComputedStyle(element);

    return (
      Css.parseTime(computed.transitionDuration) +
      Css.parseTime(computed.transitionDelay)
    );
  }

  export namespace Observer {
    export const byKind = {
      // MutationObserver doesn't support selective observation, deal with this later
      // mutation: globalThis.MutationObserver,
      intersection: globalThis.IntersectionObserver,
      resize: globalThis.ResizeObserver,
    };
    export const initKeysByKind: {
      [Kind in Observer.Kind]: Exclude<Init<Kind>, undefined> extends never
        ? []
        : UTuple.FromUnion<keyof Exclude<Init<Kind>, undefined>>;
    } = {
      intersection: ["root", "rootMargin", "threshold"],
      resize: [],
    };

    export type Kind = keyof typeof byKind;
    export type Init<K extends Kind> = ConstructorParameters<
      (typeof byKind)[K]
    >[1];
    export type Entry<K extends Kind> = Parameters<
      ConstructorParameters<(typeof byKind)[K]>[0]
    >[0][number];

    export type Options<K extends Kind> = Parameters<
      InstanceType<(typeof byKind)[K]>["observe"]
    >[1];
  }

  export const observe = (() => {
    type Key<Kind extends Observer.Kind> = UObject.Value<{
      [K in Kind]: Tuple.Type<
        [K, Record.Type<Exclude<Observer.Init<K>, undefined>> | {}]
      >;
    }>;

    const events = new EventEmitter<
      [
        UObject.Value<{
          [Kind in Observer.Kind]: [Key<Kind>, Observer.Entry<Kind>];
        }>,
      ]
    >();

    const memo = UFunction.memo((key: Key<Observer.Kind>) => {
      const [kind, init] = key;

      return new Observer.byKind[kind]((entries) => {
        for (const entry of entries) {
          events.emit(key, entry);
        }
      }, init) as ResizeObserver | IntersectionObserver; // For some reason, this expression results in only ResizeObserver without cast
    });

    const refCountByKey = new Map<Key<Observer.Kind>, number>();

    function observe<Kind extends Observer.Kind>(
      kind: Kind,
      element: HTMLElement,
      handle: (entry: Observer.Entry<Kind>) => void,
      options: Observer.Init<Kind> & Observer.Options<Kind> = {}
    ) {
      const init = Object.fromEntries(
        Object.entries(options).filter(([key]) =>
          Observer.initKeysByKind[kind].includes(key as never)
        )
      );
      const key = Tuple.from([kind, Record(init)]) as Key<Observer.Kind>;
      const observer = memo(key);
      refCountByKey.set(key, (refCountByKey.get(key) ?? 0) + 1);

      observer.observe(element, options);
      events.on(key, (entry) => {
        if (entry.target === element) handle(entry);
      });

      return {
        unobserve() {
          const refCount = refCountByKey.get(key);
          assert(refCount);
          refCountByKey.set(key, refCount - 1);

          observer.unobserve(element);

          setTimeout(() => {
            if (refCountByKey.get(key) === 0) {
              observer.disconnect();
              memo.invalidate(key);
            }
          });
        },
      };
    }

    return new Proxy(
      {},
      {
        get(_, p) {
          if (!UArray.includesLiteral(Object.keys(Observer.byKind), p))
            throw new Error(
              `UHTMLElement.observe does not have property ${String(p)}, expected one of: ${UArray.join(
                Object.keys(Observer.byKind),
                ", "
              )}`
            );

          type Kind = typeof p;

          return (
            element: HTMLElement,
            optionsOrHandle:
              | (Observer.Init<Kind> & Observer.Options<Kind>)
              | ((entry: Observer.Entry<Kind>) => void),
            maybeHandle?: (entry: Observer.Entry<Kind>) => void
          ) => {
            const options =
              typeof optionsOrHandle === "function"
                ? ({} as never)
                : optionsOrHandle || ({} as never);
            const handle =
              typeof optionsOrHandle === "function"
                ? optionsOrHandle
                : maybeHandle!;

            return observe(p, element, handle, options);
          };
        },
      }
    ) as {
      [Kind in Observer.Kind]: {
        (
          element: HTMLElement,
          handle: (entry: Observer.Entry<Kind>) => void
        ): { unobserve: () => void };
        (
          element: HTMLElement,
          options: Observer.Init<Kind> | Observer.Options<Kind>,
          handle: (entry: Observer.Entry<Kind>) => void
        ): { unobserve: () => void };
      };
    };
  })();
}

export default UHTMLElement;
